import { type Cosmetic } from '@prisma/client'
import { Link } from '@remix-run/react'
import { motion, useAnimationControls } from 'framer-motion'
import { useEffect, useRef } from 'react'
import { slugify } from '#app/utils/misc'

interface FeaturedCosmeticsProps {
	cosmetics: (Omit<Cosmetic, 'added' | 'updatedAt'> & {
		images: Record<string, string>
		added: string
		updatedAt: string
		slug: string // Add this line to include the slug
	})[]
}

export function FeaturedCosmetics({ cosmetics }: FeaturedCosmeticsProps) {
	const cosmeticsWithImages = cosmetics.filter(
		cosmetic => cosmetic.images?.smallIcon,
	)
	const controls = useAnimationControls()
	const containerRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		const animate = async () => {
			const container = containerRef.current
			if (!container) return

			const containerWidth = container.offsetWidth
			await controls.start({
				x: [-containerWidth / 2, -containerWidth],
				transition: {
					duration: 20,
					ease: 'linear',
					repeat: Infinity,
				},
			})
		}

		animate()
	}, [controls, cosmeticsWithImages])

	return (
		<section className="overflow-hidden bg-[#916aff] py-16">
			<div className="container mx-auto">
				<h2 className="mb-8 text-center text-4xl font-bold text-gray-800">
					Featured Outfits
				</h2>
				<div className="relative overflow-hidden">
					<motion.div
						ref={containerRef}
						className="flex"
						animate={controls}
						style={{ width: `${cosmeticsWithImages.length * 192}px` }}
					>
						{[...cosmeticsWithImages, ...cosmeticsWithImages].map(
							(cosmetic, index) => (
								<Link
									key={`${cosmetic.id}-${index}`}
									to={`/cosmetics/${cosmetic.slug || slugify(cosmetic.name)}`}
									className="mx-4 block w-40 flex-shrink-0"
								>
									<motion.div
										whileHover={{
											y: -10,
											scale: 1.05,
											transition: { duration: 0.2 },
										}}
										className="cursor-pointer overflow-hidden rounded-lg bg-white shadow-lg"
									>
										<motion.img
											src={cosmetic.images.smallIcon}
											alt={cosmetic.name}
											className="h-40 w-full object-cover"
											whileHover={{ scale: 1.1 }}
											transition={{ duration: 0.2 }}
										/>
										<div className="p-3">
											<h3 className="mb-1 truncate text-sm font-semibold text-gray-800">
												{cosmetic.name}
											</h3>
											<p className="line-clamp-2 text-xs text-gray-600">
												{cosmetic.description}
											</p>
										</div>
									</motion.div>
								</Link>
							),
						)}
					</motion.div>
				</div>
			</div>
		</section>
	)
}
